import React, { useState, useCallback, useEffect } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { GiWheat, GiThreeLeaves, GiPlantRoots } from 'react-icons/gi';
import { FaLeaf } from 'react-icons/fa';
import Reveal from '../Reveal/Reveal';
import '../../styles/collage.css';

const DECOR = [
  { Icon: FaLeaf, cls: 'g1' },
  { Icon: GiWheat, cls: 'g2' },
  { Icon: GiThreeLeaves, cls: 'g3' },
  { Icon: GiPlantRoots, cls: 'g4' },
  { Icon: GiThreeLeaves, cls: 'g5' },
  { Icon: FaLeaf, cls: 'g6' },
  { Icon: GiWheat, cls: 'g7' },
  { Icon: GiPlantRoots, cls: 'g8' },
];

const PUB = process.env.PUBLIC_URL;

/* Collage images live in public/collage/ as 1.jpg … 10.jpg.
   `span` controls the tile size for the varied-grid look:
   'tall' | 'wide' | 'big' | '' (normal). */
const PHOTOS = [
  { n: 1, span: 'big' },
  { n: 2, span: 'tall' },
  { n: 3, span: '' },
  { n: 4, span: 'wide' },
  { n: 5, span: '' },
  { n: 6, span: 'tall' },
  { n: 7, span: 'wide' },
  { n: 8, span: '' },
  { n: 9, span: '' },
];

const FALLBACKS = [
  'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=75',
  'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?auto=format&fit=crop&w=1200&q=75',
  'https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1200&q=75',
  'https://images.unsplash.com/photo-1444858291040-58f756a3bdd6?auto=format&fit=crop&w=1200&q=75',
];

const srcFor = (i) => `${PUB}/collage/${PHOTOS[i].n}.jpg`;

export default function Collage() {
  const [active, setActive] = useState(null); // index or null

  const close = useCallback(() => setActive(null), []);
  const go = useCallback(
    (dir) => (e) => {
      e.stopPropagation();
      setActive((i) => (i + dir + PHOTOS.length) % PHOTOS.length);
    },
    []
  );

  // Keyboard: Esc to close, arrows to navigate; lock body scroll when open
  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight')
        setActive((i) => (i + 1) % PHOTOS.length);
      else if (e.key === 'ArrowLeft')
        setActive((i) => (i - 1 + PHOTOS.length) % PHOTOS.length);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active, close]);

  return (
    <section id="collage" className="section collage">
      <div className="collage__decor" aria-hidden="true">
        {DECOR.map(({ Icon, cls }) => (
          <Icon key={cls} className={`collage__leaf ${cls}`} />
        ))}
      </div>

      <div className="container">
        <div className="collage__grid">
          {PHOTOS.map((p, i) => (
            <Reveal
              key={p.n}
              delay={Math.min((i % 4) + 1, 5)}
              className={`collage__item ${p.span ? `collage__item--${p.span}` : ''}`}
              onClick={() => setActive(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setActive(i);
              }}
            >
              <img
                src={srcFor(i)}
                alt=""
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = FALLBACKS[i % FALLBACKS.length];
                }}
              />
            </Reveal>
          ))}
        </div>
      </div>

      {active !== null && (
        <div
          className="collage__lightbox"
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="collage__lb-close"
            onClick={close}
            aria-label="Close"
          >
            <FiX />
          </button>
          <button
            className="collage__lb-nav collage__lb-nav--prev"
            onClick={go(-1)}
            aria-label="Previous image"
          >
            <FiChevronLeft />
          </button>
          <img
            className="collage__lb-img"
            src={srcFor(active)}
            alt=""
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.currentTarget.src = FALLBACKS[active % FALLBACKS.length];
            }}
          />
          <button
            className="collage__lb-nav collage__lb-nav--next"
            onClick={go(1)}
            aria-label="Next image"
          >
            <FiChevronRight />
          </button>
        </div>
      )}
    </section>
  );
}
